import { ApiClientRepository } from '../../domain/ports/ApiClientRepository';
import {
  ApiClient,
  ApiClientOptions,
  CreateApiClientData,
  CreateApiClientResult,
  ApiClientSecret,
  ApiClientDetails,
  TranslatedValue,
} from '../../domain/models/ApiClient';
import { HttpClient } from '../../../common/infrastructure/http/HttpClient';

interface ApiClientListItemDTO {
  id: string;
  clientId: string;
  clientType: string;
  name: string;
  status: TranslatedValue;
  isConfidential: boolean;
  tenantId: string | null;
  createdAt: string;
}

interface ApiClientListResponseDTO {
  data: ApiClientListItemDTO[];
}

interface ApiClientOptionDTO {
  value: string;
  label: string;
}

interface ScopeGroupDTO {
  resourceLabel: string;
  resource: string;
  actions: string[];
}

interface ApiClientOptionsResponseDTO {
  data: {
    clientTypes: ApiClientOptionDTO[];
    grantTypes: ApiClientOptionDTO[];
    scopes: ScopeGroupDTO[];
  };
}

interface CreateApiClientRequestDTO {
  name: string;
  clientType: string;
  isConfidential: boolean;
  tenantId?: string;
  description?: string;
  scopes: string[];
  grantTypes: string[];
  ttl: number;
  requiresPkce: boolean;
  refreshTokenTtl?: number;
}

interface CreateApiClientResponseDTO {
  data: {
    clientId: string;
    clientSecret: string | null;
  };
}

interface ApiClientSecretDTO {
  id: string;
  secretHint: string | null;
  status: TranslatedValue;
  expiresAt: string | null;
  createdAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
}

interface ApiClientDetailsResponseDTO {
  data: {
    id: string;
    clientId: string;
    clientType: string;
    name: string;
    status: TranslatedValue;
    isConfidential: boolean;
    tenantId: string | null;
    createdAt: string;
    description: string | null;
    scopes: string[];
    grantTypes: string[];
    ttl: number;
    requiresPkce: boolean;
    refreshTokenTtl: number | null;
    secrets: ApiClientSecretDTO[];
  };
}

interface RotateSecretResponseDTO {
  data: {
    secret: string;
  };
}

function toApiClient(dto: ApiClientListItemDTO): ApiClient {
  return {
    id: dto.id,
    clientId: dto.clientId,
    clientType: dto.clientType,
    name: dto.name,
    status: dto.status,
    isConfidential: dto.isConfidential,
    tenantId: dto.tenantId,
    createdAt: dto.createdAt,
  };
}

function toApiClientSecret(dto: ApiClientSecretDTO): ApiClientSecret {
  return {
    id: dto.id,
    secretHint: dto.secretHint,
    status: dto.status,
    expiresAt: dto.expiresAt,
    createdAt: dto.createdAt,
    revokedAt: dto.revokedAt,
    lastUsedAt: dto.lastUsedAt,
  };
}

function toApiClientDetails(data: ApiClientDetailsResponseDTO['data']): ApiClientDetails {
  return {
    id: data.id,
    clientId: data.clientId,
    clientType: data.clientType,
    name: data.name,
    status: data.status,
    isConfidential: data.isConfidential,
    tenantId: data.tenantId,
    createdAt: data.createdAt,
    description: data.description,
    scopes: data.scopes,
    grantTypes: data.grantTypes,
    tokenTtlSeconds: data.ttl,
    requiresPkce: data.requiresPkce,
    refreshTokenTtlSeconds: data.refreshTokenTtl,
    secrets: data.secrets.map(toApiClientSecret),
  };
}

export class HttpApiClientRepository implements ApiClientRepository {
  constructor(private httpClient: HttpClient) {}

  async getAll(): Promise<ApiClient[]> {
    const response = await this.httpClient.get<ApiClientListResponseDTO>('/api-clients');
    return response.data.map(toApiClient);
  }

  async getOptions(): Promise<ApiClientOptions> {
    const response = await this.httpClient.get<ApiClientOptionsResponseDTO>('/api-clients/options');
    return {
      clientTypes: response.data.clientTypes,
      grantTypes: response.data.grantTypes,
      scopes: response.data.scopes.map((scope) => ({
        resource: scope.resource,
        resourceLabel: scope.resourceLabel,
        actions: scope.actions,
      })),
    };
  }

  async create(data: CreateApiClientData): Promise<CreateApiClientResult> {
    const payload: CreateApiClientRequestDTO = {
      name: data.name,
      clientType: data.clientType,
      isConfidential: data.isConfidential,
      tenantId: data.tenantId,
      description: data.description,
      scopes: data.scopes,
      grantTypes: data.grantTypes,
      ttl: data.tokenTtlSeconds,
      requiresPkce: data.requiresPkce,
      refreshTokenTtl: data.refreshTokenTtlSeconds,
    };

    const response = await this.httpClient.post<CreateApiClientResponseDTO>(
      '/api-clients',
      payload,
    );

    return {
      clientId: response.data.clientId,
      clientSecret: response.data.clientSecret,
    };
  }

  async getOne(id: string): Promise<ApiClientDetails> {
    const response = await this.httpClient.get<ApiClientDetailsResponseDTO>(`/api-clients/${id}`);
    return toApiClientDetails(response.data);
  }

  async revokeSecret(apiClientId: string, secretId: string): Promise<void> {
    await this.httpClient.post(`/api-clients/${apiClientId}/secrets/${secretId}/revoke`);
  }

  async revokeClient(id: string): Promise<void> {
    await this.httpClient.post(`/api-clients/${id}/revoke`);
  }

  async suspendClient(id: string): Promise<void> {
    await this.httpClient.post(`/api-clients/${id}/suspension`);
  }

  async activateClient(id: string): Promise<void> {
    await this.httpClient.delete(`/api-clients/${id}/suspension`);
  }

  async rotateSecret(apiClientId: string, secretId: string): Promise<{ secret: string }> {
    const response = await this.httpClient.post<RotateSecretResponseDTO>(
      `/api-clients/${apiClientId}/secrets/${secretId}/rotate`,
    );
    return { secret: response.data.secret };
  }

  async deleteClient(id: string): Promise<void> {
    await this.httpClient.delete(`/api-clients/${id}`);
  }
}
